
import api, { sendLogoutMessage } from '@/api'
import AuthService from '@/services/support/AuthService'
import CookiesService from '@/services/support/CookiesService'
import userInfoStore from '@/store/userInfo'
import { UserInfo } from '@/types/Auth'
import { DomainSecurityLevel } from '@/types/Catalogs'
import appConfig from '@/config'
import { TOKEN_TYPE_HINT } from '@/types/Login'
import { PASSWORD_REGEXP } from '@/types/RegExpressions'
import Vue from 'vue'
import Component from 'vue-class-component'
import { APIErrorFormat } from '@/types/Errors'
import PasswordDetails from '../generic/PasswordDetails.vue'

@Component({
  name: 'ChangePassword',
  components: {
    PasswordDetails
  }
})
class ChangePassword extends Vue {
  LOGOUT_IN = 10 // seconds
  counterToLogout = 0
  form: UserInfo
  showPasswordRequirements: boolean = false
  currentPassword: string = ''
  newPassword: string = ''
  newPasswordConfirmation: string = ''
  showPassword: boolean = false
  showNewPassword: boolean = false
  showNewPasswordConfirmation: boolean = false
  successMessage: boolean = false
  errorOnChangePassword: boolean = false
  errorOnChangePasswordDescription: string = ''
  changingPassword = false
  domainLevels = DomainSecurityLevel

  created () {
    if (userInfoStore.userInfo.domain.securityLevel === undefined) {
      userInfoStore.userInfo.domain.securityLevel = DomainSecurityLevel.HIGH
    }
    this.form = userInfoStore.userInfo
  }

  /**
   * Returns the domain security level to work with
   */
  get domainSecurityLevel () {
    return this.form.domain.securityLevel
  }

  /**
   * Domain security level label
   */
  get domainSecurityLevelTranslation () {
    switch (this.domainSecurityLevel) {
      case DomainSecurityLevel.LOW:
        return 'Bajo'
      case DomainSecurityLevel.MEDIUM:
        return 'Medio'
      case DomainSecurityLevel.HIGH:
        return 'Alto'
      default:
        return 'Alto'
    }
  }

  /**
   * Manages the flag to show or hide the password value
   *
   * @param password input password flag to update
   */
  toggleShowPassword (password: 'currentPassword' | 'newPassword' | 'newPasswordConfirmation') {
    if (password === 'currentPassword') {
      this.showPassword = !this.showNewPassword
    } else if (password === 'newPassword') {
      this.showNewPassword = !this.showNewPassword
    } else if (password === 'newPasswordConfirmation') {
      this.showNewPasswordConfirmation = !this.showNewPasswordConfirmation
    }
  }

  /** @returns if the input for the current password is empty */
  get isCurrentPasswordEmpty (): boolean {
    return this.currentPassword === ''
  }

  /** @returns if the input for the new password is empty */
  get isNewPasswordEmpty (): boolean {
    return this.newPassword === ''
  }

  /** @returns if the input for the confirmation of the new password is empty */
  get isConfirmationPasswordEmpty (): boolean {
    return this.newPasswordConfirmation === ''
  }

  /**
   * Indicates if the new password input is invalid, this is
   * if it has some caracter but it doesn't meet the requirements of the security level
   * or it isn't different from the current password input
   *
   * @return if the new password is invalid
   */
  get invalidNewPassword () : boolean {
    return !this.isNewPasswordEmpty && (!this.validateNewPassword || !this.validateNewDifferentPassword)
  }

  /**
   * Indicates if the confirmation of the new password is invalid due to
   * this isn't empty and doesn't match with the new password input
   *
   * @return if the confirmation of the new password is invalid
   */
  get invalidPasswordConfirmation () : boolean {
    return !this.isConfirmationPasswordEmpty && !this.validateNewPasswordAndConfirmation
  }

  /**
   * Validates that the user has confirmed correctly the new password
   */
  get validateNewPasswordAndConfirmation () {
    if (this.isNewPasswordEmpty && this.isConfirmationPasswordEmpty) return false
    return this.newPassword === this.newPasswordConfirmation
  }

  /**
   * Validates that the current password is different from the new password
   */
  get validateNewDifferentPassword () {
    if (this.isCurrentPasswordEmpty || this.isNewPasswordEmpty) return false
    return this.currentPassword !== this.newPassword
  }

  /**
   * Validates that the new password satify the requirements
   * based on the domain security level
   */
  get validateNewPassword () {
    if (this.newPassword && this.newPassword.length > 0) {
      let domainSecurityLevelPattern
      switch (this.domainSecurityLevel) {
        case DomainSecurityLevel.LOW:
          domainSecurityLevelPattern = PASSWORD_REGEXP.low_password
          break
        case DomainSecurityLevel.MEDIUM:
          domainSecurityLevelPattern = PASSWORD_REGEXP.medium_password
          break
        case DomainSecurityLevel.HIGH:
          domainSecurityLevelPattern = PASSWORD_REGEXP.high_password
          break
        default:
          domainSecurityLevelPattern = PASSWORD_REGEXP.high_password
          break
      }
      const domainSecurityLevelRegExp = new RegExp(domainSecurityLevelPattern)
      return domainSecurityLevelRegExp.test(this.newPassword)
    }
    return false
  }

  /**
   * Revokes the JWT tokens
   */
  revokeTokenValues () {
    const accessToken = CookiesService.get(appConfig.jwtAccessTokenCookieName)
    if (accessToken) {
      AuthService.revokeToken(accessToken, TOKEN_TYPE_HINT.ACCESS_TOKEN)
    }
    const refreshToken = CookiesService.get(appConfig.refreshTokenCookieName)
    if (refreshToken) {
      AuthService.revokeToken(refreshToken, TOKEN_TYPE_HINT.REFRESH_TOKEN)
    }
  }

  async showChangePasswordModal () {
    this.$bvModal.show('change-password-confirmation')
  }

  async hideChangePasswordModal () {
    this.$bvModal.hide('change-password-confirmation')
  }

  /**
   * Calls the change password ennpoint, close the login
   * and redirect to the login view
   */
  async changePassword () {
    this.changingPassword = true
    this.hideChangePasswordModal()
    let url = `${appConfig.apiUsersUrl}me/change-password`
    let configUrl = {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    }
    let dataUrl = `old_password=${this.currentPassword}&new_password=${this.newPassword}`
    await api.post(url, dataUrl, configUrl)
      .then(() => {
        this.successMessage = true
        this.counterStart(this.LOGOUT_IN, (t:number) => { this.counterToLogout = t })
      })
      .then(() => {
        this.showPasswordRequirements = false
        let msg = document.getElementsByClassName('password-changed-message').item(0) as HTMLElement
        msg.focus()

        this.clearChangePasswordError()
        setTimeout(() => {
          sendLogoutMessage()
          this.successMessage = false
        }, 10000)
      })
      .catch((error) => {
        this.errorOnChangePassword = true
        const apiError = APIErrorFormat.getInstance(error.response.data)
        const errorValue = apiError.errors[0]
        if (errorValue.code === 3241) {
          this.errorOnChangePasswordDescription = 'La contraseña actual es incorrecta.'
        } else {
          this.errorOnChangePasswordDescription = errorValue.description
        }
        this.changingPassword = false
      })
  }

  /**
   * Resets the errors info
   */
  clearChangePasswordError () {
    this.errorOnChangePassword = false
    this.errorOnChangePasswordDescription = ''
  }

  /**
   * Timer for manages ranges of N seconds
   *
   * @param counter N seconds for timer
   * @param setCounter function that updates the counter value
   */
  counterStart (counter: number, setCounter: any) {
    setCounter(counter)
    setTimeout(() => {
      if (counter > 0) {
        this.counterStart(counter - 1, setCounter)
      }
    }, 1000)
  }
}
export default ChangePassword
